'use client';

import { TicketGroup } from '@interfaces/ticketevolution/tikets';
import { useCurrencyContext } from '@contexts/currency-context';
import { type TicketSortBy, useTicketsStore } from '@store/tickets-store';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { CurrencyDropdownV2 } from '@v2/containers/CurrencyPicker/CurrencyDropdownV2';
import {
  Button,
  Checkbox,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  ChevronDown,
  FilterFunnel01,
} from '@v2/ui';
import { cn } from '@v2/utils';
import React, { useMemo } from 'react';
import { ZONE_BUCKET_LABELS, type ZoneBucket } from '../utils/ticket-ranking';
import { useTicketFilterModel } from '../utils/use-ticket-filter-model';
import { TicketPriceRangeSlider } from './TicketPriceRangeSlider';

function preventDismissForNestedOverlay(event: {
  preventDefault: () => void;
  target: EventTarget;
}) {
  const target = event.target as Element | null;
  if (
    target?.closest?.('[data-radix-popper-content-wrapper]') ||
    target?.closest?.('[data-radix-dropdown-menu-content]') ||
    target?.closest?.('[role="listbox"]')
  ) {
    event.preventDefault();
  }
}

const ticketDrawerSheetClassName =
  'mx-auto w-full max-w-[520px] rounded-t-[24px] border-0 bg-white px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-shadowLg sm:px-6 [&>div:first-child]:mt-3 [&>div:first-child]:h-1 [&>div:first-child]:w-10 [&>div:first-child]:bg-gray-300';

const ticketChoiceButtonClassName =
  'flex items-center justify-center rounded-xl border-2 bg-white font-semibold text-gray-950 transition-[border-color,background-color,box-shadow,transform,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 active:scale-[0.98]';

const ticketSortingOptions: { name: string; value: TicketSortBy }[] = [
  { name: 'Lowest price', value: 'lowestPrice' },
  { name: 'Best value', value: 'bestValue' },
  { name: 'Best location', value: 'bestLocation' },
];

type FilterMultiSelectOption = {
  checked: boolean;
  id: string;
  label: string;
  onToggle: () => void;
};

function formatSelectionSummary(selectedLabels: string[], emptyLabel: string) {
  if (selectedLabels.length === 0) return emptyLabel;
  return selectedLabels[0];
}

function FilterMultiSelectDropdown({
  emptyLabel,
  label,
  onOpenChange,
  open,
  options,
}: {
  emptyLabel: string;
  label: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  options: FilterMultiSelectOption[];
}) {
  const selectedLabels = options
    .filter((option) => option.checked)
    .map((option) => option.label);
  const selectedCount = selectedLabels.length;
  const summary = formatSelectionSummary(selectedLabels, emptyLabel);

  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-sm font-bold text-gray-800">{label}</h3>
      <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`${label}: ${summary}`}
            className={cn(
              'flex h-12 w-full items-center justify-between rounded-xl border-2 bg-white px-3 text-left text-sm font-semibold transition-[border-color,background-color,box-shadow,transform] duration-200',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
              'active:scale-[0.99]',
              open || selectedCount > 0
                ? 'border-primary-500 bg-primary-50/30 text-gray-950'
                : 'border-gray-200 text-gray-950 hover:border-primary-500 hover:bg-primary-50'
            )}
          >
            <span
              className={cn(
                'min-w-0 truncate',
                selectedCount === 0 && 'font-medium text-gray-500'
              )}
            >
              {selectedCount === 0 ? emptyLabel : summary}
            </span>
            <span className="ml-2 flex shrink-0 items-center gap-1.5">
              {selectedCount > 0 ? (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1.5 text-[11px] font-bold text-white">
                  {selectedCount}
                </span>
              ) : null}
              <ChevronDown
                size="18"
                className={cn(
                  'text-gray-500 transition-transform duration-200',
                  open && 'rotate-180'
                )}
              />
            </span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          sideOffset={8}
          collisionPadding={16}
          data-vaul-no-drag=""
          onCloseAutoFocus={(event) => event.preventDefault()}
          className="z-[60] max-h-[min(36vh,14rem)] w-[var(--radix-dropdown-menu-trigger-width)] min-w-[12rem] overflow-y-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm"
        >
          {options.map((option) => (
            <DropdownMenuItem
              key={option.id}
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-x-3 rounded-lg px-3 py-1.5',
                option.checked && 'bg-primary-50/60'
              )}
              onSelect={(event) => {
                event.preventDefault();
                option.onToggle();
              }}
            >
              <Checkbox
                className="pointer-events-none rounded-lg bg-white"
                size="sm"
                checked={option.checked}
                tabIndex={-1}
              />
              <span className="text-sm font-semibold text-gray-800">
                {option.label}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function MobileTicketFilters({
  ticketsList,
  compact = false,
}: {
  ticketsList: TicketGroup[];
  compact?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [expandedFilter, setExpandedFilter] = React.useState<
    'location' | 'options' | null
  >(null);

  const toggleBoolean = useTicketsStore((state) => state.toggleBoolean);
  const isOnlyElectronic = useTicketsStore((state) => state.isOnlyElectronic);
  const accessibleOnly = useTicketsStore((state) => state.accessibleOnly);
  const zoneBuckets = useTicketsStore((state) => state.zoneBuckets);
  const toggleZoneBucket = useTicketsStore((state) => state.toggleZoneBucket);
  const underPriceCap = useTicketsStore((state) => state.underPriceCap);
  const setUnderPriceCap = useTicketsStore((state) => state.setUnderPriceCap);
  const selectedGroups = useTicketsStore((state) => state.selectedGroups);
  const setSelectedGroups = useTicketsStore((state) => state.setSelectedGroups);
  const sortBy = useTicketsStore((state) => state.sortby);
  const setSortBy = useTicketsStore((state) => state.setSortBy);
  const { priceRate, currency } = useCurrencyContext();
  const displayRate =
    Number.isFinite(priceRate) && priceRate > 0 ? priceRate : 1;

  const { options, activeCount, clearAll, resultCount } = useTicketFilterModel(
    ticketsList,
    { includeSortCount: true }
  );

  const locationOptions = useMemo<FilterMultiSelectOption[]>(
    () =>
      options.zones.map((zone: ZoneBucket) => ({
        id: zone,
        label: ZONE_BUCKET_LABELS[zone],
        checked: zoneBuckets.includes(zone),
        onToggle: () => toggleZoneBucket(zone),
      })),
    [options.zones, toggleZoneBucket, zoneBuckets]
  );

  const ticketOptionItems = useMemo<FilterMultiSelectOption[]>(() => {
    const items: FilterMultiSelectOption[] = [];

    if (options.underCap != null) {
      items.push({
        id: 'under-cap',
        label: `Under ${formatCurrencyV2(options.underCap, displayRate, currency, false, false)}`,
        checked: underPriceCap != null,
        onToggle: () =>
          setUnderPriceCap(underPriceCap != null ? null : options.underCap),
      });
    }

    items.push({
      id: 'electronic',
      label: 'Mobile / electronic tickets',
      checked: isOnlyElectronic,
      onToggle: () => toggleBoolean('isOnlyElectronic'),
    });

    if (options.hasAda) {
      items.push({
        id: 'accessible',
        label: 'Accessible seats only',
        checked: accessibleOnly,
        onToggle: () => toggleBoolean('accessibleOnly'),
      });
    }

    return items;
  }, [
    accessibleOnly,
    currency,
    displayRate,
    isOnlyElectronic,
    options.hasAda,
    options.underCap,
    setUnderPriceCap,
    toggleBoolean,
    underPriceCap,
  ]);

  const handleReset = () => {
    clearAll();
    setExpandedFilter(null);
  };

  return (
    <Drawer
      handleOnly
      open={isOpen}
      onOpenChange={(nextOpen) => {
        setIsOpen(nextOpen);
        if (!nextOpen) setExpandedFilter(null);
      }}
    >
      <DrawerTrigger asChild>
        <Button
          size="xs"
          aria-label="Sort and filter tickets"
          className={cn(
            'bg-white',
            compact && 'size-12 shrink-0 justify-center p-0'
          )}
          variant="secondary"
        >
          <FilterFunnel01
            className={cn(!compact && 'mr-2')}
            size="18"
            aria-hidden="true"
          />
          {!compact && 'Sort & Filter'}
          {activeCount > 0 && (
            <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-bold text-white">
              {activeCount}
            </span>
          )}
        </Button>
      </DrawerTrigger>

      <DrawerContent
        className={cn(
          ticketDrawerSheetClassName,
          'flex max-h-[min(90svh,48rem)] flex-col overflow-hidden'
        )}
        onPointerDownOutside={preventDismissForNestedOverlay}
        onFocusOutside={preventDismissForNestedOverlay}
        onInteractOutside={preventDismissForNestedOverlay}
      >
        <DrawerHeader className="shrink-0 px-2 pb-3 pt-4 text-center">
          <DrawerTitle className="text-[28px] font-extrabold leading-9 tracking-[-0.7px] text-gray-950">
            Sort & filter
          </DrawerTitle>
          <DrawerDescription className="mt-2 text-base font-medium leading-6 text-gray-700">
            Refine listings by price, location, and type
          </DrawerDescription>
          <div className="mt-2 flex justify-center">
            <CurrencyDropdownV2 className="m-0 p-0" variant="black" />
          </div>
        </DrawerHeader>

        <div
          data-vaul-no-drag
          className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-2 pb-4"
        >
          <section>
            <h3 className="mb-3 text-base font-bold text-gray-800">Sort by</h3>
            <div
              className="grid grid-cols-3 gap-2"
              role="group"
              aria-label="Sort by"
            >
              {ticketSortingOptions.map((item) => {
                const isSelected = sortBy === item.value;

                return (
                  <button
                    key={item.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => setSortBy(item.value)}
                    className={cn(
                      ticketChoiceButtonClassName,
                      'min-h-[52px] px-1.5 text-center text-[13px] leading-4 tracking-[-0.2px]',
                      isSelected
                        ? 'border-primary-500 bg-primary-50/30 text-primary-500 shadow-primaryShadow'
                        : 'border-gray-200 hover:border-primary-500 hover:bg-primary-50'
                    )}
                  >
                    {item.name}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="border-t border-gray-200 pt-5">
            <TicketPriceRangeSlider ticketsList={ticketsList} />
          </section>

          {locationOptions.length > 0 || ticketOptionItems.length > 0 ? (
            <section className="border-t border-gray-200 pt-5">
              <div
                className={cn(
                  'grid gap-3',
                  locationOptions.length > 0 && ticketOptionItems.length > 0
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
                )}
              >
                {locationOptions.length > 0 ? (
                  <FilterMultiSelectDropdown
                    label="Seat location"
                    emptyLabel="Any location"
                    options={locationOptions}
                    open={expandedFilter === 'location'}
                    onOpenChange={(nextOpen) =>
                      setExpandedFilter(nextOpen ? 'location' : null)
                    }
                  />
                ) : null}
                {ticketOptionItems.length > 0 ? (
                  <FilterMultiSelectDropdown
                    label="Ticket options"
                    emptyLabel="Any options"
                    options={ticketOptionItems}
                    open={expandedFilter === 'options'}
                    onOpenChange={(nextOpen) =>
                      setExpandedFilter(nextOpen ? 'options' : null)
                    }
                  />
                ) : null}
              </div>
              {selectedGroups.length > 0 ? (
                <button
                  type="button"
                  className="mt-3 min-h-11 px-1 text-left text-sm font-semibold text-primary-600"
                  onClick={() => setSelectedGroups([])}
                >
                  Clear map section selection
                </button>
              ) : null}
            </section>
          ) : null}
        </div>

        <DrawerFooter className="mt-0 shrink-0 gap-3 border-t border-gray-200 bg-white px-2 pb-0 pt-4">
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="secondary"
              className="min-h-12 w-full rounded-xl text-base font-semibold"
              type="button"
              disabled={activeCount === 0}
              onClick={handleReset}
            >
              Reset
            </Button>
            <DrawerClose asChild>
              <Button
                variant="primary"
                className="min-h-12 w-full rounded-xl text-base font-semibold"
                type="button"
              >
                Show {resultCount} {resultCount === 1 ? 'result' : 'results'}
              </Button>
            </DrawerClose>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
